import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const FILTERS = [
  { key: "everything", label: "EVERYTHING" },
  { key: "video", label: "VIDEO" },
  { key: "commission", label: "COMMISSION" },
  { key: "installation", label: "INSTALLATION" },
  { key: "performance", label: "PERFORMANCE" },
];

const TV_TRANSITION = "/video/tv-transition.webm";
const TV_STATICS = [
  "/video/tv-01.webm",
  "/video/tv-02.webm",
  "/video/tv-03.webm",
  "/video/tv-04.webm",
  "/video/tv-05.webm",
  "/video/tv-06.webm",
  "/video/tv-07.webm",
  "/video/tv-08.webm",
];

function getGridColumnCount() {
  if (typeof window === "undefined") return 4;
  const width = window.innerWidth;
  if (width <= 768) return 1;
  if (width <= 992) return 2;
  if (width <= 1200) return 3;
  return 4;
}

function waitForMetadata(video) {
  if (video.readyState >= 1) return Promise.resolve();
  return new Promise((resolve) => {
    video.addEventListener("loadedmetadata", resolve, { once: true });
  });
}

function waitForPaintedFrame(video) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    if (typeof video.requestVideoFrameCallback === "function") {
      video.requestVideoFrameCallback(() => finish());
    } else {
      video.addEventListener("playing", finish, { once: true });
      video.addEventListener("canplay", finish, { once: true });
    }

    // Fallback if the browser never fires frame callbacks.
    setTimeout(finish, 120);
  });
}

function applyActiveStyles(video, isActive) {
  if (!video) return;
  if (isActive) {
    video.style.position = "relative";
    video.style.opacity = "1";
    video.style.zIndex = "1";
    video.style.pointerEvents = "auto";
  } else {
    video.style.position = "absolute";
    video.style.left = "0";
    video.style.top = "0";
    video.style.opacity = "0";
    video.style.zIndex = "0";
    video.style.pointerEvents = "none";
  }
}

// Dual-buffer loop: keep a standby copy primed at frame 0 and swap before
// the active clip hits its blank end-frame (common WebM loop flicker).
function SeamlessLoopVideo({ src }) {
  const aRef = useRef(null);
  const bRef = useRef(null);
  const activeIndexRef = useRef(0);
  const swappingRef = useRef(false);

  useEffect(() => {
    const videos = [aRef.current, bRef.current];
    if (!videos[0] || !videos[1]) return undefined;

    let cancelled = false;
    let standbyWarmed = false;
    swappingRef.current = false;
    activeIndexRef.current = 0;
    applyActiveStyles(videos[0], true);
    applyActiveStyles(videos[1], false);

    // Start the swap early enough that a cold CDN decode still has time
    // to paint the standby frame before the active clip ends.
    const SWAP_BEFORE_END = 0.45;
    const media = { objectUrl: null };

    const prepare = async () => {
      let mediaSrc = src;
      try {
        const response = await fetch(src);
        const blob = await response.blob();
        if (cancelled) return;
        media.objectUrl = URL.createObjectURL(blob);
        mediaSrc = media.objectUrl;
      } catch {
        // Fall back to the public URL if blob caching fails.
      }

      if (cancelled) {
        if (media.objectUrl) URL.revokeObjectURL(media.objectUrl);
        return;
      }

      videos.forEach((video) => {
        video.src = mediaSrc;
        video.muted = true;
        video.playsInline = true;
        video.preload = "auto";
        video.load();
      });

      await Promise.all(videos.map(waitForMetadata));
      if (cancelled) return;

      const duration = videos[0].duration;
      const start =
        Number.isFinite(duration) && duration > 0.2
          ? Math.random() * duration * 0.85
          : 0;

      videos[0].currentTime = start;
      videos[1].currentTime = 0;
      videos[1].pause();
      await videos[0].play().catch(() => {});
    };

    prepare();

    const handleTimeUpdate = async (event) => {
      const video = event.target;
      const index = videos.indexOf(video);
      if (index !== activeIndexRef.current || swappingRef.current) return;
      if (!video.duration || !Number.isFinite(video.duration)) return;

      const nextIndex = 1 - index;
      const nextVideo = videos[nextIndex];
      const remaining = video.duration - video.currentTime;

      // Warm the standby decoder mid-clip so the swap is cheap.
      if (!standbyWarmed && video.currentTime >= video.duration * 0.4) {
        standbyWarmed = true;
        try {
          nextVideo.currentTime = 0;
        } catch {
          // Ignore seek errors while warming.
        }
      }

      if (remaining > SWAP_BEFORE_END) return;

      swappingRef.current = true;

      try {
        nextVideo.currentTime = 0;
        await nextVideo.play().catch(() => {});
        await waitForPaintedFrame(nextVideo);
        if (cancelled) return;

        // Swap visibility synchronously so the standby frame covers the
        // active clip before it can paint a blank loop frame.
        applyActiveStyles(nextVideo, true);
        applyActiveStyles(video, false);
        activeIndexRef.current = nextIndex;
        standbyWarmed = false;

        video.pause();
        try {
          video.currentTime = 0;
        } catch {
          // Ignore seek errors on torn-down media.
        }
        swappingRef.current = false;
      } catch {
        swappingRef.current = false;
      }
    };

    videos.forEach((video) => {
      video.addEventListener("timeupdate", handleTimeUpdate);
    });

    return () => {
      cancelled = true;
      videos.forEach((video) => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.pause();
        video.removeAttribute("src");
        video.load();
      });
      if (media.objectUrl) URL.revokeObjectURL(media.objectUrl);
    };
  }, [src]);

  return (
    <VideoStack className="art-tv-stack">
      <StackedVideo ref={aRef} muted playsInline />
      <StackedVideo ref={bRef} muted playsInline />
    </VideoStack>
  );
}

function ArtMedia({
  image,
  index,
  hoverArt,
  isMobile,
  isFilteredOut,
  onRevealChange,
  claimTvSrc,
  releaseTvSrc,
}) {
  const [phase, setPhase] = useState("image");
  const [staticSrc, setStaticSrc] = useState(null);
  const [transitionToken, setTransitionToken] = useState(0);
  const prevFilteredRef = useRef(isFilteredOut);
  const afterTransitionRef = useRef("static");
  const phaseRef = useRef(phase);
  const onRevealChangeRef = useRef(onRevealChange);

  phaseRef.current = phase;
  onRevealChangeRef.current = onRevealChange;

  useEffect(() => {
    onRevealChangeRef.current?.(phase === "image");
  }, [phase]);

  // Only acts when filtered-out status changes, so pieces that stay
  // filtered out keep their TV clip when switching between filters.
  useEffect(() => {
    if (prevFilteredRef.current === isFilteredOut) {
      return undefined;
    }

    prevFilteredRef.current = isFilteredOut;
    let cancelled = false;

    if (isFilteredOut) {
      // Thumbnail -> staggered TV transition -> static noise
      afterTransitionRef.current = "static";
      const delay = Math.random() * 2500;
      const timeoutId = setTimeout(() => {
        if (cancelled) return;
        setTransitionToken((token) => token + 1);
        setPhase("transition");
      }, delay);
      return () => {
        cancelled = true;
        clearTimeout(timeoutId);
      };
    }

    // If the piece never left the thumbnail, skip the restore transition.
    if (phaseRef.current === "image") {
      return undefined;
    }

    // Static noise -> staggered TV transition -> thumbnail
    afterTransitionRef.current = "image";
    const delay = Math.random() * 2500;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      setTransitionToken((token) => token + 1);
      setPhase("transition");
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isFilteredOut]);

  const handleTransitionEnded = () => {
    if (afterTransitionRef.current === "static") {
      setStaticSrc(claimTvSrc(index));
      setPhase("static");
      return;
    }

    releaseTvSrc(index);
    setStaticSrc(null);
    setPhase("image");
  };

  if (phase === "transition") {
    return (
      <Video
        key={`transition-${transitionToken}`}
        src={TV_TRANSITION}
        autoPlay
        muted
        playsInline
        onEnded={handleTransitionEnded}
      />
    );
  }

  if (phase === "static" && staticSrc) {
    return <SeamlessLoopVideo key={staticSrc} src={staticSrc} />;
  }

  return (
    <Img
      src={isMobile || index === hoverArt ? image.hoverSrc : image.src}
      alt={image.alt}
    />
  );
}

function ArtPortfolio() {
  const [hoverArt, setHoverArt] = useState(-1);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );
  const [activeFilter, setActiveFilter] = useState("everything");
  const [revealedBySrc, setRevealedBySrc] = useState({});
  const tvSrcByIndexRef = useRef({});

  const claimTvSrc = (index) => {
    const assigned = tvSrcByIndexRef.current;
    const cols = getGridColumnCount();
    const forbidden = new Set();

    if (index % cols !== 0 && assigned[index - 1]) {
      forbidden.add(assigned[index - 1]);
    }
    if ((index + 1) % cols !== 0 && assigned[index + 1]) {
      forbidden.add(assigned[index + 1]);
    }

    const pool = TV_STATICS.filter((src) => !forbidden.has(src));
    const choices = pool.length > 0 ? pool : TV_STATICS;
    const picked = choices[Math.floor(Math.random() * choices.length)];

    tvSrcByIndexRef.current = { ...assigned, [index]: picked };
    return picked;
  };

  const releaseTvSrc = (index) => {
    if (!(index in tvSrcByIndexRef.current)) return;
    const next = { ...tvSrcByIndexRef.current };
    delete next[index];
    tvSrcByIndexRef.current = next;
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setVisible(true);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);

    updateIsMobile();
    mediaQuery.addEventListener("change", updateIsMobile);
    return () => mediaQuery.removeEventListener("change", updateIsMobile);
  }, []);

  const images = [
    {
      src: "/images/art-portfolio/mulroney-still-01-450.jpg",
      alt: "Ladies and Gentlemen, Brian Mulroney",
      hoverSrc: "/images/art-portfolio/mulroney-gif-01-450.gif",
      title: "Ladies and Gentlemen, Brian Mulroney",
      description: "Video commission, 2026",
      length: "Directed by Matthew Rankin",
      commission: true,
      video: true,
    },
    {
      src: "/images/art-portfolio/halloween-still-01-450.jpg",
      alt: "I Remember Halloween",
      hoverSrc: "/images/art-portfolio/halloween-gif-01-450.gif",
      title: "Halloween",
      description: "Installation, 2024/2025",
      length: "Lopez, Montréal",
      installation: true,
    },
    {
      src: "/images/art-portfolio/svprembobinez-still-02-450.jpg",
      alt: "SVP Rembobinez",
      hoverSrc: "/images/art-portfolio/svprembobinez-gif-02-450.gif",
      description: "Multi-location installation, 2024",
      length: "Montreal, Val-David, Québec City",
      link: "https://sites.google.com/view/svprembobinez/?pli=1&authuser=0",
      externallink: true,
      installation: true,
    },
    {
      src: "/images/art-portfolio/unstable-still-01-450.jpg",
      alt: "Unstable Intermediated Forms",
      hoverSrc: "/images/art-portfolio/unstable-gif-01-450.gif",
      title: "Unstable Intermediated Forms",
      description: "Installation, 2021",
      length: "TOPO Gallery, Montréal",
      installation: true,
    },
    {
      src: "/images/art-portfolio/cablebox-still-01-450.jpg",
      alt: "Cable Box",
      hoverSrc: "/images/art-portfolio/cablebox-gif-01-450.gif",
      title: "Cable Box",
      description: "Video, 2020",
      length: "16min, 45sec",
      link: "/art-portfolio/cable-box",
      video: true,
    },
    {
      src: "/images/art-portfolio/pivot-still-01-450.jpg",
      alt: "Pivot, Forward, Collapse",
      hoverSrc: "/images/art-portfolio/pivot-gif-01-450.gif",
      title: "Pivot, Forward, Collapse",
      description: "Video / Installation, 2020",
      length: "2min 51sec",
      link: "/art-portfolio/pivot-forward-collapse",
      video: true,
    },
    {
      src: "/images/art-portfolio/adultswim-still-01-450.jpg",
      alt: "Off the Air 5x04: Analog Video Remix",
      hoverSrc: "/images/art-portfolio/adultswim-gif-01-450.gif",
      description: "Television / Web Broadcast, 2018",
      length: "11min 51sec",
      link: "/art-portfolio/off-the-air",
      video: true,
      commission: true,
    },
    {
      src: "/images/art-portfolio/carmen-still-01-450.jpg",
      alt: "Carmen",
      hoverSrc: "/images/art-portfolio/carmen-gif-01-450.gif",
      title: "Carmen",
      description: "Home Video Release / Performance, 2018/2020",
      length: "14min, 52sec",
      link: "/art-portfolio/carmen",
      video: true,
      performance: true,
    },
    {
      src: "/images/art-portfolio/noban-still-01-450.jpg",
      alt: "NO BAN",
      hoverSrc: "/images/art-portfolio/noban-gif-01-450.gif",
      title: "NO BAN",
      description: "Video, 2017",
      length: "4min 49sec",
      video: true,
    },
    {
      src: "/images/art-portfolio/videoart-still-01-450.jpg",
      alt: "Video Art for Conditional Malaise",
      hoverSrc: "/images/art-portfolio/videoart-gif-01-450.gif",
      description: "Performance / Home Video Release / Installation, 2017",
      length: "29min 04sec",
      link: "/art-portfolio/video-art",
      video: true,
    },
    {
      src: "/images/art-portfolio/burningdesire-still-01-450.jpg",
      alt: "The Burning Desire in a Dollar Bill",
      hoverSrc: "/images/art-portfolio/burningdesire-gif-01-450.gif",
      description: "Video, 2017",
      length: "5min 06sec",
      link: "/art-portfolio/burning-desire",
      video: true,
    },
    {
      src: "/images/art-portfolio/expo67-still-01-450.jpg",
      alt: "Man and His Progress",
      hoverSrc: "/images/art-portfolio/expo67-gif-01-450.gif",
      description: "Installation, 2017",
      length: "Eastern Bloc, Montréal",
      link: "/art-portfolio/man-and-his-progress",
      installation: true,
    },
    {
      src: "/images/art-portfolio/antigonick-still-01-450.jpg",
      alt: "Antigonick",
      hoverSrc: "/images/art-portfolio/antigonick-gif-01-450.gif",
      title: "Antigonick",
      description: "Live projections, 2016",
      length: "Directed by Will Aitken, written by Anne Carson",
      performance: true,
      commission: true,
    },
    {
      src: "/images/art-portfolio/prtautology-still-01-450.jpg",
      alt: "Puerto Rico Tautology (14 dubs high)",
      hoverSrc: "/images/art-portfolio/prtautology-gif-01-450.gif",
      description: "Video, 2016",
      length: "6min 59sec",
      link: "/art-portfolio/puerto-rico-tautology",
      video: true,
    },
    {
      src: "/images/art-portfolio/garfield-still-01-450.jpg",
      alt: "ǝɹnʇuǝʌp∀ uǝǝʍollɐH s,plǝᴉɟɹɐפ",
      hoverSrc: "/images/art-portfolio/garfield-gif-01-450.gif",
      description: "Home Video Release, 2015",
      length: "24min 52sec",
      link: "https://bleunuitvideo.bandcamp.com/merch/n-u-p-u-oll-h-s-pl-vhs",
      externallink: true,
      video: true,
    },
  ];

  return (
    <Page className={visible ? "fade-in" : ""}>
      <FilterBar>
        {FILTERS.map((filter) => (
          <FilterButton
            key={filter.key}
            type="button"
            $active={activeFilter === filter.key}
            onClick={() => setActiveFilter(filter.key)}
          >
            {filter.label}
          </FilterButton>
        ))}
      </FilterBar>
      <Wrapper>
        {images.map((image, index) => {
          const isFilteredOut =
            !isMobile &&
            activeFilter !== "everything" &&
            !image[activeFilter];
          const isRevealed = revealedBySrc[image.src] !== false;
          const showDetails = !isFilteredOut && isRevealed;
          const linkProps = {
            to: image.link,
            ...(image.externallink && {
              target: "_blank",
              rel: "noopener noreferrer",
            }),
          };
          const handleRevealChange = (isRevealedNow) => {
            setRevealedBySrc((prev) =>
              prev[image.src] === isRevealedNow
                ? prev
                : { ...prev, [image.src]: isRevealedNow }
            );
          };

          const media = (
            <ArtMedia
              image={image}
              index={index}
              hoverArt={hoverArt}
              isMobile={isMobile}
              isFilteredOut={isFilteredOut}
              onRevealChange={handleRevealChange}
              claimTvSrc={claimTvSrc}
              releaseTvSrc={releaseTvSrc}
            />
          );

          return (
            <ArtDiv
              key={image.src}
              onMouseOver={() =>
                !isMobile && showDetails && setHoverArt(index)
              }
              onMouseOut={() => setHoverArt(-1)}
            >
              {image.link ? (
                <Link
                  {...linkProps}
                  onClick={(event) => {
                    if (!showDetails) event.preventDefault();
                  }}
                  style={{ pointerEvents: showDetails ? "auto" : "none" }}
                >
                  {media}
                </Link>
              ) : (
                media
              )}
              {showDetails &&
                (image.link ? (
                  <StyledLink {...linkProps}>
                    <TextDiv>
                      <H2>{image.alt}</H2>
                      <P>{image.description}</P>
                      <P>{image.length}</P>
                    </TextDiv>
                  </StyledLink>
                ) : (
                  <TextDiv>
                    <H2>{image.alt}</H2>
                    <P>{image.description}</P>
                    <P>{image.length}</P>
                  </TextDiv>
                ))}
            </ArtDiv>
          );
        })}
      </Wrapper>
    </Page>
  );
}

const Page = styled.div`
  width: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
  &.fade-in {
    opacity: 1;
  }
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const FilterButton = styled.button`
  font-size: 1.5em;
  font-family: inherit;
  font-weight: bold;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${(props) => (props.$active ? "red" : "black")};
  transition: all 0.3s ease-in-out;
  &:hover {
    color: red;
  }
  &:active {
    text-decoration: dashed;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 40px;
  box-sizing: border-box;
  @media screen and (max-width: 768px) {
    width: 100%;
    gap: 24px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:visited {
    color: black;
  }
`;

const ArtDiv = styled.div`
  flex: 1 1 calc(25% - 30px);
  max-width: calc(25% - 30px);
  min-width: 260px;
  margin: 0 0 20px 0px;
  box-sizing: border-box;
  img,
  video {
    transition: transform 0.5s;
  }

  &:hover img,
  &:hover > video,
  &:hover > a > video,
  &:hover .art-tv-stack {
    transform: zoom(1.1);
  }
  @media screen and (max-width: 1200px) {
    flex-basis: calc(33.333% - 27px);
    max-width: calc(33.333% - 27px);
  }
  @media screen and (max-width: 992px) {
    flex-basis: calc(50% - 20px);
    max-width: calc(50% - 20px);
    min-width: 220px;
  }
  @media screen and (max-width: 768px) {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0;

    img,
    video {
      transition: none;
    }

    &:hover img,
    &:hover > video,
    &:hover > a > video,
    &:hover .art-tv-stack {
      transform: none;
    }
  }
`;

const mediaStyles = `
  border-radius: 5%;
  display: block;
  width: 100%;
  height: auto;
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const Img = styled.img`
  ${mediaStyles}
`;

const Video = styled.video`
  ${mediaStyles}
`;

const VideoStack = styled.div`
  position: relative;
  width: 100%;
  border-radius: 5%;
  overflow: hidden;
  line-height: 0;
  transition: transform 0.5s;
`;

const StackedVideo = styled.video`
  ${mediaStyles}
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  z-index: 0;
  pointer-events: none;

  &:first-of-type {
    position: relative;
    opacity: 1;
    z-index: 1;
    pointer-events: auto;
  }
`;

const TextDiv = styled.div`
  /* width: 60%; */
`;

const H2 = styled.h2`
  font-size: 1.5em;
  margin: 2% 0 0 0;
`;

const P = styled.p`
  font-size: 1.25em;
  margin: 1% 0 5px 0;
`;

export default ArtPortfolio;
